/**
 * Ficha de uma estação de radiossonda: os índices, o Skew-T Log-P e o percurso
 * do balão.
 *
 * A primeira versão desenhava as duas curvas em eixos retos, por achar o
 * Skew-T ilegível para quem não é da área. O usuário pediu o diagrama de
 * verdade (28/09/2026), com as áreas de energia — está em `SkewT.tsx`.
 *
 * No padrão das outras fichas: categoria, nome, números, gráfico, rodapé só
 * com o link da fonte.
 */
import type { ReactNode } from 'react';
import { ExternalLink } from 'lucide-react';
import { formatLonLat } from '../lib/format';
import { formatDateTimeBoth } from '../lib/time';
import { urlSkewT } from './api';
import { shownTimeOf, useTimeStore } from '../state/time';
import { alturaDaBase, corAltura, corCape, derivaKm, estadoDe, FAIXAS_CAPE, faixaCape, niveisNoInstante, perfilNoInstante, tetoM } from './perfil';
import { useSoundingStore } from './store';
import type { SondagemFeature } from './layer';
import { SkewT } from './SkewT';
import type { Indices, PontoTrilha } from './types';

export { kmNoNivel } from './km';

/**
 * Percurso horizontal do balão, visto de cima.
 *
 * Existe porque no mapa a trilha tem uns 5 px: 20 km de deriva numa vista de
 * 3.800 km. Aqui ela aparece na escala dela, com os números ao lado — esticar
 * o desenho no mapa para ficar visível seria mentir sobre a distância.
 */
function Percurso({ trilha }: { trilha: readonly PontoTrilha[] }) {
  const km = derivaKm(trilha);
  const teto = tetoM(trilha);
  if (km === null || km < 0.1) return null;

  const xs = trilha.map((p) => p.x);
  const ys = trilha.map((p) => p.y);
  const x0 = Math.min(...xs);
  const y0 = Math.min(...ys);
  const larg = Math.max(1e-6, Math.max(...xs) - x0);
  const alt = Math.max(1e-6, Math.max(...ys) - y0);
  // Mesma escala nos dois eixos: o formato do percurso é a informação.
  const esc = Math.max(larg, alt);
  const S = 58;
  const pos = (p: PontoTrilha) => ({
    x: ((p.x - x0) / esc) * S + (S - (larg / esc) * S) / 2,
    // Latitude cresce para o norte; no SVG y cresce para baixo.
    y: S - (((p.y - y0) / esc) * S + (S - (alt / esc) * S) / 2),
  });

  return (
    <div className="flex items-center gap-2.5 pt-1">
      <svg viewBox={`0 0 ${S} ${S}`} width={S} height={S} aria-hidden className="shrink-0 rounded bg-white/5">
        {trilha.slice(1).map((p, i) => {
          const a = pos(trilha[i]!);
          const b = pos(p);
          return <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke={corAltura(p.z)} strokeWidth="1.6" strokeLinecap="round" />;
        })}
        {/* Lançamento. */}
        <circle cx={pos(trilha[0]!).x} cy={pos(trilha[0]!).y} r="2" fill="#fff" opacity="0.9" />
      </svg>
      <div className="text-[11px] leading-snug text-ink-300">
        <div>
          Terminou a <span className="tabular-nums text-white">{km.toFixed(0)} km</span> da estação
          {teto !== null && (
            <>
              , estourando a <span className="tabular-nums text-white">{(teto / 1000).toFixed(0)} km</span>
            </>
          )}
        </div>
        <div className="text-[10px] leading-snug text-ink-400">Percurso visto de cima; a cor sobe da superfície ao topo.</div>
      </div>
    </div>
  );
}

/** Um número da grade: o rótulo pequeno em cima, o valor embaixo. */
function Dado(props: { rotulo: string; dica: string; children: ReactNode }) {
  return (
    <div className="min-w-0" title={props.dica}>
      <dt className="truncate text-[10px] leading-tight text-ink-400">{props.rotulo}</dt>
      <dd className="truncate text-xs leading-snug font-medium text-ink-100 tabular-nums">{props.children}</dd>
    </div>
  );
}

const inteiro = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
const tem = (v: number | undefined): v is number => typeof v === 'number' && Number.isFinite(v);

/** Os índices como o Wyoming os publica. Índice ausente não aparece. */
function IndicesDaSondagem({ indices }: { indices: Indices }) {
  const base = alturaDaBase(indices);
  const itens = [
    tem(indices.MUCAPE) && (
      <Dado key="cape" rotulo="CAPE" dica={`Energia de convecção da parcela mais instável — ${FAIXAS_CAPE[faixaCape(indices.MUCAPE)]!.rotulo}.`}>
        <span style={{ color: corCape(indices.MUCAPE) }}>{inteiro(indices.MUCAPE)}</span> <span className="font-normal text-ink-400">J/kg</span>
      </Dado>
    ),
    tem(indices.MUCIN) && (
      <Dado key="cin" rotulo="CIN" dica="Inibição convectiva: a energia que falta para a parcela subir sozinha.">
        {inteiro(indices.MUCIN)} <span className="font-normal text-ink-400">J/kg</span>
      </Dado>
    ),
    tem(indices.PWAT) && (
      <Dado key="pwat" rotulo="Água precipitável" dica="Quanta chuva o ar tem para dar, se toda a umidade da coluna precipitasse.">
        {inteiro(indices.PWAT)} mm
      </Dado>
    ),
    base !== null && (
      <Dado key="base" rotulo="Base da nuvem" dica="Altura em que a convecção começa a condensar (nível de condensação).">
        {inteiro(base)} m
      </Dado>
    ),
    tem(indices.KINX) && (
      <Dado key="k" rotulo="Índice K" dica="Potencial de tempestade pela umidade e pelo resfriamento com a altura.">
        {inteiro(indices.KINX)}
      </Dado>
    ),
    tem(indices.TOTL) && (
      <Dado key="tt" rotulo="Total Totals" dica="Instabilidade entre 850 e 500 hPa.">
        {inteiro(indices.TOTL)}
      </Dado>
    ),
  ].filter(Boolean);
  if (!itens.length) return null;
  return (
    <dl className="grid grid-cols-3 gap-x-3 gap-y-1.5 border-t border-white/10 pt-1.5" data-indices="">
      {itens}
    </dl>
  );
}

export function SoundingPopup({ dado }: { dado: SondagemFeature }) {
  const { estacao } = dado;
  // A ficha acompanha o player, como a dos pluviômetros: a sondagem é a do
  // instante exibido e, com o balão subindo, o gráfico se monta até ele.
  const instante = useTimeStore((s) => shownTimeOf(s));
  const perfis = useSoundingStore((s) => s.dados.perfisDe.get(estacao.wmo));
  const perfil = perfis ? perfilNoInstante(perfis, instante) : dado.perfil;
  const estado = perfis ? estadoDe(perfil, instante) : dado.estado;
  const noInstante = perfil ? niveisNoInstante(perfil, instante) : null;

  return (
    <div className="w-[min(21rem,80vw)] space-y-1.5" data-ficha-sondagem="">
      <div className="pr-5 text-xs text-ink-400">Radiossonda · Wyoming</div>
      <div className="leading-tight font-semibold text-white">
        {estacao.nome}
        <div className="text-xs font-normal text-ink-300">
          {estacao.uf} · WMO {estacao.wmo}
        </div>
      </div>

      {estado === 'erro' ? (
        <div className="text-xs text-yellow-200">Não foi possível buscar esta estação: {perfil?.erro}</div>
      ) : estado === 'ausente' || !perfil || perfil.ms === null ? (
        <div className="text-sm text-ink-300">Sem sondagem nas últimas 48 h.</div>
      ) : (
        <>
          <div className="text-[11px] text-ink-400">
            Lançamento de {formatDateTimeBoth(perfil.ms)}
            {estado === 'antiga' && <span className="text-yellow-200"> · anterior ao período exibido</span>}
          </div>

          <IndicesDaSondagem indices={perfil.indices} />

          <div className="border-t border-white/10 pt-1 text-ink-200">
            <SkewT niveis={noInstante!.niveis} todos={perfil.niveis} alturaDoBalao={noInstante!.alturaDoBalao} lat={estacao.lat} />
          </div>

          {perfil.trilha && <Percurso trilha={perfil.trilha} />}
        </>
      )}

      <div className="border-t border-white/10 pt-1 text-[11px] text-ink-400">
        {formatLonLat(estacao.lon, estacao.lat)}
        {perfil?.ms !== null && perfil?.ms !== undefined && (
          <>
            {' · '}
            <a
              href={urlSkewT(estacao.wmo, perfil.ms)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-rio-300 hover:underline"
            >
              Skew-T no Wyoming
              <ExternalLink size={10} />
            </a>
          </>
        )}
      </div>
    </div>
  );
}
